/**
 * <size-guide-popup> — the product page's size guide, as a popup.
 *
 * Markup: snippets/size-guide-popup.liquid, rendered by sections/product.liquid
 * once per matched `size_guide` block. Triggers are any control carrying
 * `data-size-guide-trigger="<popup id>"` — today the block's own link.
 *
 * THE SAME SHAPE AS assets/image-lightbox.js, deliberately: page-inert for focus
 * containment, counted scroll lock, `inert` in the markup for the closed state,
 * focus to the panel rather than to a control, one-directional mutual
 * exclusion. The reasoning for each lives in that file and is not repeated
 * here; where this differs, the difference is commented.
 *
 * THE TRIGGER IS A REAL LINK TO THE FULL GUIDE PAGE. Without this script it
 * simply navigates there, which is the no-JavaScript fallback. A modified click
 * (new tab, etc.) is also left alone to navigate.
 *
 * LOAD ORDER: after mobile-menu.js, mega-menu.js, search-overlay.js,
 * cart-drawer.js and filter-drawer.js, for the same reason as the lightbox.
 */
if (!customElements.get('size-guide-popup')) {
  customElements.define(
    'size-guide-popup',
    class SizeGuidePopup extends HTMLElement {
      connectedCallback() {
        this.panel = this.querySelector('[data-size-guide-panel]');
        this.scrim = this.querySelector('[data-size-guide-scrim]');
        this.closeButton = this.querySelector('[data-size-guide-close]');

        if (!this.panel) {
          console.warn('[size-guide-popup] markup is incomplete; popup disabled');
          return;
        }

        this.isOpen = false;
        this.lastTrigger = null;
        this.inerted = [];

        this.handleTriggerClick = this.handleTriggerClick.bind(this);
        this.handleCloseClick = this.handleCloseClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
        this.handleBlockDeselect = this.handleBlockDeselect.bind(this);

        // Id-scoped: a product with two guides (rings, necklaces) has two popups.
        this.triggers = document.querySelectorAll(
          '[data-size-guide-trigger="' + CSS.escape(this.id) + '"]'
        );

        this.triggers.forEach((trigger) => {
          trigger.addEventListener('click', this.handleTriggerClick);

          // ARIA here, not in Liquid: without this script the link just navigates.
          trigger.setAttribute('aria-haspopup', 'dialog');
          trigger.setAttribute('aria-expanded', 'false');
          trigger.setAttribute('aria-controls', this.id);
        });

        this.closeButton?.addEventListener('click', this.handleCloseClick);
        this.scrim?.addEventListener('click', this.handleCloseClick);
        document.addEventListener('keydown', this.handleKeydown);

        /**
         * THEME EDITOR ONLY: selecting the block in the sidebar opens its popup,
         * so the merchant can see the content they are editing. These events
         * never fire on the live storefront.
         */
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
        document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
      }

      disconnectedCallback() {
        this.triggers?.forEach((trigger) => {
          trigger.removeEventListener('click', this.handleTriggerClick);
        });
        this.closeButton?.removeEventListener('click', this.handleCloseClick);
        this.scrim?.removeEventListener('click', this.handleCloseClick);
        document.removeEventListener('keydown', this.handleKeydown);
        document.removeEventListener('shopify:block:select', this.handleBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);

        // Torn down while open (theme editor re-render): don't strand the page locked.
        if (this.isOpen) {
          this.releaseScroll();
          this.setPageInert(false);
          this.removeAttribute('data-open');
        }
      }

      open() {
        if (this.isOpen) return;

        this.renderImage();

        // Order is load-bearing — see the same block in assets/image-lightbox.js.
        document.querySelector('mobile-menu')?.close?.();
        document.querySelector('mega-menu')?.close?.();
        document.querySelector('search-overlay')?.close?.();
        document.querySelector('cart-drawer')?.close?.();
        document.querySelector('filter-drawer')?.close?.();

        this.setOpen(true);
        this.panel.focus();
      }

      close() {
        if (!this.isOpen) return;

        this.setOpen(false);

        if (this.lastTrigger && this.lastTrigger.isConnected) this.lastTrigger.focus();
        this.lastTrigger = null;
      }

      /**
       * Copy the image out of its <template> on first open — see the note in the
       * Liquid for why it is not in the page from the start. Kept afterwards: it
       * is one image, and re-opening should not flash an empty half.
       */
      renderImage() {
        const media = this.querySelector('[data-size-guide-media]');
        const template = media?.querySelector('template[data-size-guide-image]');
        if (!media || !template || media.querySelector('img')) return;

        media.appendChild(template.content.cloneNode(true));
      }

      /** The single state funnel. Nothing else touches these attributes. */
      setOpen(next) {
        if (next === this.isOpen) return;
        this.isOpen = next;

        this.toggleAttribute('data-open', next);
        this.toggleAttribute('inert', !next);

        this.triggers?.forEach((trigger) => {
          trigger.setAttribute('aria-expanded', String(next));
        });

        this.setPageInert(next);

        if (next) {
          this.lockScroll();
        } else {
          this.releaseScroll();
        }
      }

      /** Every top-level region of the page inert except this one — see image-lightbox.js. */
      setPageInert(on) {
        if (!on) {
          this.inerted.forEach((element) => element.removeAttribute('inert'));
          this.inerted = [];
          return;
        }

        const keep = this.topLevelAncestor(this);

        Array.prototype.forEach.call(document.body.children, (element) => {
          if (element === keep) return;
          if (element.hasAttribute('inert')) return;

          element.setAttribute('inert', '');
          this.inerted.push(element);
        });
      }

      /** Walk up to whichever child of <body> contains the given node. */
      topLevelAncestor(node) {
        while (node.parentElement && node.parentElement !== document.body) {
          node = node.parentElement;
        }
        return node;
      }

      /** Through the shared, counted controller; direct fallback if scroll.js failed. */
      lockScroll() {
        if (window.themeScroll) {
          this.lockedController = true;
          window.themeScroll.lock('size-guide-popup');
          return;
        }

        this.lockedController = false;
        document.documentElement.classList.add('overflow-hidden');
      }

      releaseScroll() {
        if (this.lockedController && window.themeScroll) {
          window.themeScroll.unlock('size-guide-popup');
        } else {
          document.documentElement.classList.remove('overflow-hidden');
        }
        this.lockedController = false;
      }

      handleTriggerClick(event) {
        // A real link: new-tab and other modified clicks go to the full page.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

        event.preventDefault();
        this.lastTrigger = event.currentTarget;
        this.open();
      }

      handleCloseClick(event) {
        event.preventDefault();
        this.close();
      }

      handleKeydown(event) {
        if (event.key !== 'Escape' || !this.isOpen) return;
        this.close();
      }

      handleBlockSelect(event) {
        if (event.detail?.blockId !== this.dataset.blockId) return;
        this.open();
      }

      handleBlockDeselect(event) {
        if (event.detail?.blockId !== this.dataset.blockId) return;
        this.close();
      }
    }
  );
}
